import config from 'config';

export function createSession(parameters) {
    const {
        accessToken, accessTokenTtl, refreshToken, refreshTokenTtl, authorizationCode, state, redirectUri, isResetPasswordEntered, domainSessionUuid,
    } = parameters;
    createCookieAccess(accessToken, accessTokenTtl);
    createCookieRefresh(refreshToken, refreshTokenTtl);
    createCookieDomainSessionUuid(domainSessionUuid);
    redirectsToAdmin(authorizationCode, state, redirectUri, isResetPasswordEntered);
}

/**
 * Session handoff for an admin SSO login (CM-1805).
 *
 * Writes NO cookies. auth-service sets VfGAccess/VfGRefresh on the exchange response itself, and
 * this only sends the admin onward.
 *
 * That split is not stylistic. VfGRefresh is HttpOnly — heimdall marks every cookie but VfGAccess
 * that way — and a browser silently ignores a document.cookie write against an existing HttpOnly
 * cookie. An earlier version of this function wrote both from the response body; the access cookie
 * landed, the refresh cookie did not, and a returning admin kept whatever refresh token was already
 * in the jar. Usually that was one a previous console logout had revoked, so every refresh came
 * back "Refresh token not found" and the console retried in a loop. The write reports no error, and
 * a first-time browser with no VfGRefresh works fine, so it broke only returning admins and only in
 * a real browser.
 *
 * It does NOT go through redirectsToAdmin. That path appends `?code=&state=` for Viafoura-Core's
 * /oauth2/code handshake, and SSO has no authorization code to hand over: the design deliberately
 * dropped the code mint, so the console authenticates from the cookie on each request instead
 * (CM-1793). Reusing redirectsToAdmin here would send `code=undefined` to a PHP endpoint that
 * exists only to consume one.
 *
 * There is no loginSessionUuid either: auth-service does not issue one on this path.
 *
 * The destination is the console, NOT `adminEntryUrl`. The bare legacy admin root resolves to
 * site 0 — the global area — and Admin_controller gates it on
 * `hasSitePrivilege(0, PRIVILEGE_MOD) || hasRootLevelAccess()`. A Viafoura operator passes on root
 * access; a customer's admin, who is exactly who SSO exists for, holds privileges on their own
 * site and none on site 0. So every SSO login was refused with
 * `error_oauth=unauthorized_access` and then bounced between ever-growing `referrer` URLs.
 *
 * Verified on vf-dev2 2026-09-02 with a real SSO token: `/` refused, while `/admin/3033/3033/`
 * (that admin's own site) and the console both returned 200 — the token and the privileges were
 * right all along, and only the landing URL was wrong.
 *
 * The console is also where a password login ends up, since Viafoura-Core's oauth2 controller
 * redirects there after /oauth2/code. This makes the two login paths agree rather than inventing
 * a third destination.
 */
export function createSsoSession() {
    window.location = config.consoleUrl;
}

function createCookieAccess(accessToken, accessTokenTtl) {
    const cookie = [];
    cookie.push(`VfGAccess=${accessToken}`);
    // Heimdall does not always report a token lifetime, and the server-side cookie builder
    // already guards for that (Oauth2TokenHandler#buildCookie). Without the same guard here a
    // missing ttl writes `Max-Age=undefined`, which browsers reject — dropping the cookie
    // entirely rather than falling back to a session cookie.
    if (accessTokenTtl !== undefined && accessTokenTtl !== null) {
        cookie.push(`Max-Age=${accessTokenTtl}`);
    }
    cookie.push(`Domain=${config.cookieDomain}`);
    cookie.push('Path=/');
    cookie.push('Secure');
    cookie.push('SameSite=Lax');
    document.cookie = cookie.join('; ');
}

function createCookieRefresh(refreshToken, refreshTokenTtl) {
    const cookie = [];
    cookie.push(`VfGRefresh=${refreshToken}`);
    // Heimdall does not always report a token lifetime, and the server-side cookie builder
    // already guards for that (Oauth2TokenHandler#buildCookie). Without the same guard here a
    // missing ttl writes `Max-Age=undefined`, which browsers reject — dropping the cookie
    // entirely rather than falling back to a session cookie.
    if (refreshTokenTtl !== undefined && refreshTokenTtl !== null) {
        cookie.push(`Max-Age=${refreshTokenTtl}`);
    }
    cookie.push(`Domain=${config.cookieDomain}`);
    cookie.push('Path=/');
    cookie.push('Secure');
    cookie.push('SameSite=Lax');
    document.cookie = cookie.join('; ');
}

function createCookieDomainSessionUuid(domainSessionUuid) {
    const cookie = [];
    cookie.push(`loginSessionUuid=${domainSessionUuid}`);
    cookie.push('Max-Age=604800');
    cookie.push(`Domain=${config.cookieDomain}`);
    cookie.push('Path=/');
    cookie.push('Secure');
    cookie.push('SameSite=Lax');
    document.cookie = cookie.join('; ');
}

function redirectsToAdmin(authorizationCode, state, redirectUri, isResetPasswordEntered) {
    const baseUrl = redirectUri || config.adminUrl;
    let url = `${baseUrl}?code=${authorizationCode}&state=${state}`;
    if (isResetPasswordEntered) {
        url += '&reset_password_entered=y';
    }
    window.location = url;
}
